// components/FAQSection.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

const sizes = {
  mobileM: '375px',
  mobileL: '425px',
  tablet: '768px',
  desktop: '1800px',
};

export const device = {
  tablet: `(max-width: ${sizes.tablet})`,
  desktop: `(min-width: ${sizes.desktop})`,
};

const FAQComponent = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      q: 'What is BrandAstra?',
      a: 'BrandAstra is an AI-powered consumer intelligence platform that helps brands decode consumer sentiment, track engagement, and predict market trends across 50+ digital channels.',
    },
    {
      q: 'How does BrandAstra work?',
      a: 'Simply build a query, select your channels, and hit "Run." Our AI scans digital conversations in real-time to deliver actionable insights through customizable dashboards, reports, and alerts.',
    },
    {
      q: 'Do I need technical expertise to use BrandAstra?',
      a: 'No! BrandAstra is designed for marketers, agencies, and brand managers—just ask a question, and our AI-powered assistant helps you get the insights you need. ',
    },
    {
      q: 'What kind of brands can use BrandAstra?',
      a: 'Any brand that wants to understand its audience better! From fashion to fintech, our platform works across industries to provide deep consumer intelligence.',
    },
    {
      q: 'What data sources does BrandAstra analyze?',
      a: 'We aggregate insights from social media, news, forums, e-commerce platforms, and more—over 50+ digital channels in real-time.',
    },
    {
      q: 'How does BrandAstra track sentiment and engagement?',
      a: 'Our AI analyzes consumer conversations to identify emotions, reactions, and engagement levels, helping you understand the "why" behind consumer sentiment.',
    },
    {
      q: 'Can I compare my brand’s performance with competitors?',
      a: 'Yes! Our Competitive Intelligence feature allows you to track competitor brand perception and benchmark against industry leaders.',
    },
    {
      q: 'Does BrandAstra offer influencer identification?',
      a: 'Absolutely! We help you discover key voices shaping conversations in your industry, so you can find the right influencers for your brand.',
    },
    {
      q: 'How customizable are the insights and dashboards?',
      a: 'Highly customizable! You can tailor dashboards, filters, and reports to focus on what matters most to your business.',
    },
    {
      q: 'Can I integrate BrandAstra with my existing tools?',
      a: 'Yes! We provide seamless integration options for reporting and collaboration with your team’s existing workflow.',
    },
    {
      q: 'How much does BrandAstra cost?',
      a: 'We offer flexible pricing based on your needs. Check out our pricing page to compare packages and features.',
    },
    {
      q: 'Is there a free trial available?',
      a: 'Yes! Get started with a limited free trial to experience the platform before upgrading to a paid plan.',
    },
    {
      q: 'Does BrandAstra comply with data privacy regulations?',
      a: 'Yes, we prioritize data security and privacy compliance to ensure your brand operates within legal frameworks.',
    },
    {
      q: 'How secure is my data on BrandAstra?',
      a: 'BrandAstra operates as a SaaS platform with a multi-tenant architecture, ensuring that your data remains securely isolated. Access is strictly limited to authorized team members.',
    },
    {
      q: 'What kind of support does BrandAstra provide?',
      a: 'We provide tutorial videos and a self-help knowledge base as soon as you log in. For enterprise clients, we offer dedicated account managers for personalized assistance.',
    },
    {
      q: 'Can I request a demo?',
      a: 'Of course! Book a demo with our team to see BrandAstra in action or send an email with your requirement to info@brandastra.ai.',
    },
  ];

  return (
    <Wrapper>
      <Heading>FAQ</Heading>
      <FlexGrid>
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <FAQCard onClick={() => toggle(index)} key={index}>
              <Question>
                {faq.q}
                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </Question>
              {isOpen && <Answer>{faq.a}</Answer>}
            </FAQCard>
          );
        })}
      </FlexGrid>
    </Wrapper>
  );
};

export default FAQComponent;

// Styled Components

const Wrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  background: #e6f0f0;
  padding-bottom: 60px;
`;

const Heading = styled.h2`
  font-family: Inter;
  font-weight: 600;
  font-size: 32px;
  margin-top: unset;
  text-align: center;
  padding: 40px 0 20px;
  background-image: linear-gradient(90deg, #063257 0%, #006867 100%);
  color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
  @media only screen and ${device.tablet} {
    font-size: 28px;
    padding-bottom: 10px;
  }
`;

const FlexGrid = styled.div`
  column-count: 2;
  column-gap: 20px;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 20px;

  @media only screen and ${device.tablet} {
    column-count: 1;
    padding: 0 10px;
  }
`;

const FAQCard = styled.div`
  background: linear-gradient(
    90.1deg,
    rgba(138, 186, 185, 0.6) 0.09%,
    rgba(176, 208, 208, 0.3) 99.91%
  );
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
  display: inline-block;
  width: 94%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    transform: translateY(-2px);
  }
  @media only screen and ${device.tablet} {
    width: 345px;
    padding-right: 5px;
    padding-left: 7px;
  }
`;

const Question = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #121212;
  text-align: left;
  margin-bottom: 8px;
`;

const Answer = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #12121299;
  text-align: left;
  transition: all 0.3s ease-in-out;
`;
